/**
 * In-taskpane confirmation overlay
 * Reusable modal that resolves true on confirm, false on cancel/backdrop/Escape.
 * Does not use window.confirm (not supported in Excel add-ins).
 */

/* global document */

export interface ShowConfirmOptions {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
}

/**
 * Show the confirmation overlay.
 * Resolves true when the user confirms, false when they cancel, click outside, or press Escape.
 * Cleans up event listeners when done. If required DOM elements are missing, resolves false.
 */
export function showConfirm(options: ShowConfirmOptions): Promise<boolean> {
  const overlay = document.getElementById("confirm-overlay");
  const titleEl = document.getElementById("confirm-overlay-title");
  const messageEl = document.getElementById("confirm-overlay-message");
  const cancelBtn = document.getElementById("confirm-overlay-cancel");
  const confirmBtn = document.getElementById("confirm-overlay-confirm");
  const backdrop = overlay?.querySelector(".confirm-backdrop");

  if (!overlay || !titleEl || !messageEl || !cancelBtn || !confirmBtn) {
    return Promise.resolve(false);
  }

  titleEl.textContent = options.title;
  messageEl.textContent = options.message;
  const confirmLabel = options.confirmLabel ?? "Refresh";
  const cancelLabel = options.cancelLabel ?? "Cancel";
  const confirmLabelSpan = confirmBtn.querySelector(".ms-Button-label");
  const cancelLabelSpan = cancelBtn.querySelector(".ms-Button-label");
  if (confirmLabelSpan) {
    confirmLabelSpan.textContent = confirmLabel;
  }
  if (cancelLabelSpan) {
    cancelLabelSpan.textContent = cancelLabel;
  }

  overlay.classList.remove("confirm-overlay-hidden");

  return new Promise<boolean>((resolve) => {
    let settled = false;
    const finish = (value: boolean) => {
      if (settled) return;
      settled = true;
      overlay.classList.add("confirm-overlay-hidden");
      cleanup();
      resolve(value);
    };

    const onConfirm = () => finish(true);
    const onCancel = () => finish(false);
    const onBackdrop = (e: Event) => {
      if (e.target === backdrop) finish(false);
    };
    const onEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") finish(false);
    };

    cancelBtn.addEventListener("click", onCancel);
    confirmBtn.addEventListener("click", onConfirm);
    backdrop?.addEventListener("click", onBackdrop);
    document.addEventListener("keydown", onEscape);

    const cleanup = () => {
      cancelBtn.removeEventListener("click", onCancel);
      confirmBtn.removeEventListener("click", onConfirm);
      backdrop?.removeEventListener("click", onBackdrop);
      document.removeEventListener("keydown", onEscape);
    };
  });
}
